import { JENTIS_COUNTRIES, JENTIS_COUNTRY_MAP } from './constants';

/**
 * Checks if a given country code is supported by JENTIS tracking.
 *
 * @param countryCode - The ISO country code (e.g., 'AT', 'DE').
 * @returns `true` if the country is supported; otherwise, `false`.
 */
export function isValidJentisCountry(countryCode: string): boolean {
  return JENTIS_COUNTRIES.includes(countryCode);
}

/**
 * Builds the inline script string required to load the JENTIS tracking script.
 *
 * @param host - The CDN host for the JENTIS script (e.g., 'cdn.jentis.com').
 * @param project - The JENTIS project name.
 * @param fileName - The script filename to load (e.g., 'jentis.js').
 * @returns The full inline script string that should be injected.
 */
function getJentisScript(host: string, project: string, fileName: string): string {
  return `(function (sCDN,sCDNProject,sCDNWorkspace,sCDNVers) {
        if( window.localStorage !== null && typeof window.localStorage === "object" &&
            typeof window.localStorage.getItem === "function" && window.sessionStorage !== null &&
            typeof window.sessionStorage === "object" && typeof window.sessionStorage.getItem === "function"
        ) {
            sCDNVers = window.sessionStorage.getItem('jts_preview_version') || window.localStorage.getItem('jts_preview_version') || sCDNVers;
        }
        window.jentis = window.jentis || {};
        window.jentis.config = window.jentis.config || {};
        window.jentis.config.frontend = window.jentis.config.frontend || {};
        window.jentis.config.frontend.cdnhost = sCDN+"/get/"+sCDNWorkspace+"/web/"+sCDNVers+"/";
        window.jentis.config.frontend.vers = sCDNVers;
        window.jentis.config.frontend.env = sCDNWorkspace;
        window.jentis.config.frontend.project = sCDNProject;
        window._jts = window._jts || [];

        var f   = document.getElementsByTagName("script")[0];
        var j = document.createElement("script");
        j.async = true;
        j.src = window.jentis.config.frontend.cdnhost+"${fileName}";
        f.parentNode.insertBefore(j, f)
    })("${host}","${project}","live","_");
    `;
}

/**
 * Gets the JENTIS script string for a specific country based on predefined configuration.
 *
 * @param country - The ISO country code (e.g., 'AT', 'DE').
 * @returns The script string to be injected for the country.
 * @throws Will throw an error if the country is not found in the configuration map.
 */
export function getJentisScriptByCountry(country: string): string {
  const config = JENTIS_COUNTRY_MAP[country.toUpperCase()];

  if (!config) {
    throw new Error(`No JENTIS config found for country code: ${country}`);
  }

  const { host, fileName, project } = config;

  return getJentisScript(host, project, fileName);
}
